/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router'

import {
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiPageHeader,
  EuiSkeletonText,
  EuiSpacer,
  EuiTextColor,
  EuiTitle,
} from '@elastic/eui'

import { useGetSaasUserQuery } from '@modules/cloud-lib/users/hooks'
import Breadcrumbs from '@modules/cui/Breadcrumbs/Breadcrumbs'
import CopyButton from '@modules/cui/CopyButton'
import type { ProjectID } from '@modules/project-admin-api/v1/types'
import AdminBoostWindow from '@modules/project-admin-components/BoostWindow'
import ProjectActionsButton from '@modules/project-admin-components/ProjectActionsButton'
import KubernetesCluster from '@modules/project-admin-components/ProjectPage/KubernetesCluster'
import ObservabilityDashboardLink from '@modules/project-admin-components/ProjectPage/ObservabilityDashboardLink'
import ObservabilityDiscoverLink from '@modules/project-admin-components/ProjectPage/ObservabilityDiscoverLink'
import OpenProjectButton from '@modules/project-admin-components/ProjectPage/OpenProjectButton'
import ViewOrganizationButton from '@modules/project-admin-components/ProjectPage/ViewOrganizationButton'
import AdminSearchPower from '@modules/project-admin-components/SearchPower'
import { useGetProjectQuery } from '@modules/project-admin-lib/hooks/get'
import { projectCrumbs } from '@modules/project-admin-pages/breadcrumbs'
import DataRetentionMessage from '@modules/project-user-components/DataRetention/DataRetention'
import { getDisplayProjectId } from '@modules/project-lib/display'
import genericMessages from '@modules/project-lib/genericMessages'
import type { ProjectType } from '@modules/ui-types/projects'
import { userOverviewUrl } from '@modules/urls/users'
import { toLocaleDateTime } from '@modules/utils/format'
import history from '@modules/utils/history'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import AdminProjectPageTitle from '@modules/project-admin-components/ProjectPage/Title'
import ProjectTypeIconAndLabel from '@modules/project-components/ProjectList/ProjectTypeIconAndLabel'
import { useListRegionsQuery } from '@modules/cluster-admin-lib/hooks'
import FormattedRegionAndProvider from '@modules/project-components/FormattedRegionAndProvider'

type Props = {
  projectType: ProjectType
}

const AdminProjectPage: React.FunctionComponent<Props> = ({ projectType }) => {
  const { projectId } = useParams<{ projectId: ProjectID }>()

  const projectQuery = useGetProjectQuery(projectType, projectId)
  const { data: project } = projectQuery

  const userQuery = useGetSaasUserQuery(project?.metadata.created_by, {
    enabled: Boolean(project),
  })
  const { data: user } = userQuery

  const { data: regions, isLoading: regionsIsLoading } = useListRegionsQuery()

  const headerRightSideItems = project
    ? [
        <ProjectActionsButton project={project} />,
        <OpenProjectButton project={project} />,
        <ViewOrganizationButton project={project} />,
      ]
    : []

  return (
    <div>
      <Breadcrumbs
        breadcrumbs={projectCrumbs({
          projectType,
          projectId,
          projectName: project?.name,
        })}
      />

      <EuiPageHeader
        bottomBorder={true}
        pageTitle={<AdminProjectPageTitle project={project} />}
        rightSideGroupProps={{ gutterSize: 'm' }}
        rightSideItems={headerRightSideItems}
      >
        {project && (
          <EuiFlexGroup gutterSize='s' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiTextColor color='subdued'>Project ID</EuiTextColor>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <span>{getDisplayProjectId(project.id)}</span>
            </EuiFlexItem>
            <EuiFlexItem grow={false} style={{ marginTop: -2 }}>
              <CopyButton asLink={true} value={project.id}>
                <EuiIcon type='copyClipboard' />
              </CopyButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}
      </EuiPageHeader>
      <EuiSpacer />

      {renderContent()}
    </div>
  )

  function renderContent() {
    if (projectQuery.error) {
      return <ProjectApiErrorCallout error={projectQuery.error} />
    }

    if (projectQuery.isLoading || regionsIsLoading) {
      return <EuiSkeletonText />
    }

    if (!project) {
      return null
    }

    const showPerformanceControls = project.type === `elasticsearch`

    const timestamps = [
      {
        title: <FormattedMessage {...genericMessages.createdAt} />,
        description: (
          <Fragment>
            <EuiSpacer size='xs' />
            {project.metadata.created_at && (
              <div>{toLocaleDateTime(project.metadata.created_at)}</div>
            )}
          </Fragment>
        ),
      },
    ]

    if (project.metadata.deleted_at) {
      timestamps.push({
        title: <FormattedMessage {...genericMessages.deletedAt} />,
        description: (
          <Fragment>
            <EuiSpacer size='xs' />
            <div>{toLocaleDateTime(project.metadata.deleted_at)}</div>
          </Fragment>
        ),
      })
    }

    return (
      <Fragment>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiDescriptionList
              compressed={true}
              listItems={[
                {
                  title: <FormattedMessage {...genericMessages.projectType} />,
                  description: (
                    <Fragment>
                      <EuiSpacer size='xs' />
                      <ProjectTypeIconAndLabel project={project} />
                    </Fragment>
                  ),
                },
              ]}
            />
            <EuiSpacer size='l' />
            <EuiDescriptionList
              compressed={true}
              listItems={[
                {
                  title: <FormattedMessage id='project.label.csp' defaultMessage='CSP details' />,
                  description: (
                    <FormattedRegionAndProvider regionId={project.region_id} allRegions={regions} />
                  ),
                },
              ]}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiDescriptionList
              compressed={true}
              listItems={[
                {
                  title: (
                    <FormattedMessage
                      id='project.label.kubernetesCluster'
                      defaultMessage='Kubernetes cluster'
                    />
                  ),
                  description: (
                    <Fragment>
                      <EuiSpacer size='xs' />
                      <KubernetesCluster project={project} />
                    </Fragment>
                  ),
                },
              ]}
            />
            <EuiSpacer size='l' />
            <EuiDescriptionList
              compressed={true}
              listItems={[
                {
                  title: (
                    <FormattedMessage
                      id='project.label.controlPlaneLogsMetrics'
                      defaultMessage='Control Plane logs / metrics'
                    />
                  ),
                  description: (
                    <Fragment>
                      <EuiSpacer size='xs' />
                      <ObservabilityDashboardLink
                        project={project}
                        dashboard='serverless-control-plane-services-logs'
                      />
                    </Fragment>
                  ),
                },
              ]}
            />
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiDescriptionList
              compressed={true}
              listItems={[
                {
                  title: (
                    <FormattedMessage
                      id='project.label.projectLogsMetrics'
                      defaultMessage='Project logs / metrics'
                    />
                  ),
                  description: (
                    <Fragment>
                      <EuiSpacer size='xs' />
                      <ObservabilityDashboardLink
                        project={project}
                        dashboard='serverless-project-id-overview'
                      />
                      <EuiSpacer size='xs' />
                      <ObservabilityDashboardLink
                        project={project}
                        dashboard='serverless-project-applications-logs'
                      />
                      <EuiSpacer size='xs' />
                      <ObservabilityDiscoverLink project={project} query='proxyLogs' />
                      <EuiSpacer size='xs' />
                      <ObservabilityDashboardLink
                        project={project}
                        dashboard='serverless-es-project-overview'
                      />
                      <EuiSpacer size='xs' />
                      <ObservabilityDashboardLink
                        project={project}
                        dashboard='serverless-es-project-stack-monitoring'
                      />
                    </Fragment>
                  ),
                },
              ]}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiDescriptionList compressed={true} listItems={timestamps} />
            <EuiSpacer size='l' />
            <EuiDescriptionList
              compressed={true}
              listItems={[
                {
                  title: <FormattedMessage {...genericMessages.createdBy} />,
                  description: (
                    <Fragment>
                      <EuiSpacer size='xs' />
                      {user ? (
                        <EuiLink
                          style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          onClick={() => history.push(userOverviewUrl(project.metadata.created_by))}
                        >
                          {user.user.email || user.user.user_id}
                        </EuiLink>
                      ) : (
                        <span>{project.metadata.created_by}</span>
                      )}
                    </Fragment>
                  ),
                },
              ]}
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size='xxl' />

        <EuiTitle size='s'>
          <h2>
            <FormattedMessage
              id='project.heading.searchLakeSettings'
              defaultMessage='Search AI Lake Settings'
            />
          </h2>
        </EuiTitle>

        {showPerformanceControls && (
          <Fragment>
            <EuiSpacer size='l' />
            <AdminSearchPower project={project} />
            <EuiSpacer size='l' />
            <AdminBoostWindow project={project} />
          </Fragment>
        )}

        <EuiSpacer size='l' />
        <DataRetentionMessage project={project} />
      </Fragment>
    )
  }
}

export default AdminProjectPage
